import { CSSProperties, ReactNode } from "react";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { ProgressBase } from "./base/progress-base";

class ProgressBar extends ProgressBase<any, any> {
  constructor(props: any) {
    super(props);
  }

  public static defaultProps = {
    direction: "horizontal"
  };

  getBackgroundImage = () => {
    const { firstGradientColor, secondGradientColor, thirdGradientColor, direction } = this.props;
    const percent = this.getPercent();
    const gradientDirection = direction === "vertical" ? "to bottom" : "to right";
    if (percent === 0 || !percent || percent < 0) {
      return `transparent`;
    }
    if (
      firstGradientColor &&
      firstGradientColor !== "" &&
      secondGradientColor &&
      secondGradientColor !== "" &&
      thirdGradientColor &&
      thirdGradientColor !== ""
    ) {
      return `linear-gradient(${gradientDirection}, ${firstGradientColor} 0%, ${secondGradientColor} 50%, ${thirdGradientColor} 100%)`;
    }
    if (firstGradientColor && firstGradientColor !== "" && secondGradientColor && secondGradientColor !== "") {
      return `linear-gradient(${gradientDirection}, ${firstGradientColor} 0%, ${secondGradientColor} 100%)`;
    }
  };

  getBackground = () => {
    const percent = this.getPercent();
    if (percent === 0 || !percent || percent < 0) {
      return `transparent`;
    }
    return this.props.style?.backgroundColor && this.props.style?.backgroundColor !== ""
      ? this.props.style?.backgroundColor
      : "#246eff";
  };

  getSizeDimension = () => {
    const { size } = this.props;
    switch (size) {
      case "large":
        return 24;
      case "medium":
        return 16;
      default:
        return 8;
    }
  };

  getSizeBorderRadius = () => {
    const { size } = this.props;
    switch (size) {
      case "large":
        return 12;
      case "medium":
        return 8;
      default:
        return 4;
    }
  };

  getInnerBorderRadius = () => {
    const { size, direction } = this.props;
    const percent = this.getPercent();

    if (direction === "vertical") {
      switch (size) {
        case "large":
          return percent === 0 || !percent || percent < 0 ? 12 : "12px 12px 0 0";
        case "medium":
          return percent === 0 || !percent || percent < 0 ? 8 : "8px 8px 0 0";
        default:
          return percent === 0 || !percent || percent < 0 ? 4 : "4px 4px 0 0";
      }
    } else {
      switch (size) {
        case "large":
          return percent === 0 || !percent || percent < 0 ? 12 : "0 12px 12px 0";
        case "medium":
          return percent === 0 || !percent || percent < 0 ? 8 : "0 8px 8px 0";
        default:
          return percent === 0 || !percent || percent < 0 ? 4 : "0 4px 4px 0";
      }
    }
  };

  getTextStyle = () => {
    const { style } = this.props;
    return {
      color: style?.color,
      fontSize: style?.fontSize,
      fontWeight: style?.fontWeight,
      fontFamily: style?.fontFamily,
      lineHeight: style?.lineHeight,
      letterSpacing: style?.letterSpacing,
      textDecoration: style?.textDecoration,
      textTransform: style?.textTransform
    };
  };

  getStyle = () => {
    const { style, firstGradientColor, secondGradientColor, thirdGradientColor, size, direction } = this.props;
    const newStyle = { ...style };
    if (firstGradientColor && firstGradientColor !== "" && secondGradientColor && secondGradientColor !== "") {
      newStyle.backgroundImage = this.getBackgroundImage();
    } else {
      newStyle.backgroundColor = this.getBackground();
    }
    newStyle.position = "relative";

    if (direction === "vertical") {
      newStyle.width = this.getSizeDimension();
      newStyle.height = this.props.style?.height ?? "100%";
      newStyle.minHeight = this.props.style?.minHeight ?? 100;
    } else {
      newStyle.width = this.props.style?.width ?? "100%";
      newStyle.height = this.getSizeDimension();
    }

    newStyle.borderRadius = this.getSizeBorderRadius();
    return newStyle;
  };

  getPercent = () => {
    const { percent } = this.props;
    if (window.kuika.isDesignTime && typeof percent === "string") return 33;
    if (this.props.value) {
      return this.props.value.percent !== undefined
        ? parseFloat(this.props.value.percent)
        : parseFloat(this.props.value);
    }
    return parseFloat(percent);
  };

  render(): JSX.Element {
    const { direction = "horizontal", hidePercentage, percentageDirection } = this.props;
    const percent = this.getPercent();
    const isVertical = direction === "vertical";

    const alignItems =
      (this.props?.style?.textAlign as string)?.replace("-webkit-", "") === "right"
        ? "flex-end"
        : (this.props?.style?.textAlign as string)?.replace("-webkit-", "") === "left"
          ? "flex-start"
          : "center";

    const containerStyle: CSSProperties = {
      display: isVertical ? (this.props?.style?.display === "inline" ? "inline-flex" : "flex") : "flex",
      gap: 8,
      ...(isVertical
        ? {
            flexDirection: "column" as const,
            alignItems: alignItems as "center" | "flex-start" | "flex-end"
          }
        : {
            alignItems: "center"
          })
    };

    const innerDivStyle: CSSProperties = isVertical
      ? {
          position: "absolute" as const,
          left: 0,
          right: 0,
          top: 0,
          height: `calc(100% - ${percent < 0 || !percent ? 0 : percent}%)`,
          background: "#e9e9e9",
          borderRadius: this.getInnerBorderRadius()
        }
      : {
          position: "absolute" as const,
          right: percent === 0 || !percent ? 0 : "-2px",
          top: 0,
          bottom: 0,
          width: `calc(100% - ${percent < 0 || !percent ? 0 : percent}%)`,
          background: "#e9e9e9",
          borderRadius: this.getInnerBorderRadius()
        };

    return (
      <div
        data-testid="progress-base"
        data-percent={percent !== undefined ? percent : "undefined"}
        data-direction={direction}
        data-size={this.props.size || ""}
        data-hide-percentage={hidePercentage !== undefined ? String(hidePercentage) : ""}
        data-percentage-direction={percentageDirection || ""}
        className={this.props.className}
        id={this.props.id}
        style={containerStyle}
      >
        <div style={this.getStyle()}>
          <div style={innerDivStyle}></div>
        </div>
        {!hidePercentage &&
          percent !== null &&
          percent !== undefined &&
          (percentageDirection === "left" ? (
            <span style={this.getTextStyle()}>%{percent}</span>
          ) : (
            <span style={this.getTextStyle()}>{percent}%</span>
          ))}
      </div>
    );
  }
}

const progressBar = withCommonEvents(ProgressBar);
export { progressBar as ProgressBar };
